<script lang="ts" setup>
import { computed, ref } from 'vue';
import { Menu as AMenu, MenuItem, SubMenu } from 'ant-design-vue';
import { any, bool } from 'vue-types';
// import { useRoute } from 'vue-router';

import HarmonLogo from '@/ui/Svg/HarmonLogo.vue';
import HarmonLogoIcon from '@/ui/Svg/HarmonLogoIcon.vue';
import useAgent from '@/providers/useAgent';
import { getConfig } from '~/config/constants';

const config = getConfig(useRuntimeConfig());

/* const getSelectedKey = (currentURL: string, routes: SubMenuProps[]) => {
  for (let i = 0; i < routes.length; i += 1) {
    const { items } = routes[i];
    for (let j = 0; j < items.length; j += 1) {
      const item = items[j];
      if (currentURL.includes(item.link)) {
        return item.link;
      }
    }
  }
  return '';
};
const selectedKeys = ref<string[]>([getSelectedKey(route.path, menus)]);
*/

defineProps({
  class: any(),
  collapsed: bool(),
});

const openKeys = ref([]);
const {
  superUserRights,
  ppAdminRights,
  implementationRights,
  tuitionRights,
  rentersRights,
  bulkImplementationRolloverRights,
  tuitionMissingTransactionsRights,
  fileImportRights,
  licensedRights,
} = useAgent();
const isSuperUser = computed(() => superUserRights());
const idPpAdminRights = computed(() => ppAdminRights());
const idImplementationRights = computed(() => implementationRights());
const isTuitionImpl = computed(() => tuitionRights());
const isRentersImpl = computed(() => rentersRights());
const hasBulkImplementationRolloverRight = computed(() => bulkImplementationRolloverRights());
const canAccessMissingTransactions = computed(
  () => isSuperUser.value || tuitionMissingTransactionsRights(),
);
const canAccessFileImports = computed(() => fileImportRights());
const isProduction = computed(() => config.isProd);
const showImplementationRollover = computed(
  () => hasBulkImplementationRolloverRight.value && !isProduction.value,
);
</script>
<template>
  <div class="flex flex-col relative">
    <div v-if="collapsed" class="flex items-center justify-center py-3">
      <div class="w-11">
        <HarmonLogoIcon />
      </div>
    </div>
    <div v-else class="px-5 pt-3 pb-4">
      <span alt="GradGuard Logo" class="block h-16 w-36">
        <HarmonLogo />
      </span>
    </div>
    <AMenu
      v-model:open-keys="openKeys"
      mode="inline"
      :class="$props.class"
      style="border: none"
      :inline-collapsed="collapsed"
    >
      <SubMenu key="Members" title="Members">
        <template #icon>
          <FaIcon :icon="['fas', 'user-group']" />
        </template>
        <MenuItem key="/users">
          <NuxtLink to="/users" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'users']" />
            </span>
            <span>
              Users
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/renters-purchase">
          <NuxtLink to="/renters-purchase" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'house']" />
            </span>
            <span>
              Renters Purchase
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/tuition-purchase">
          <NuxtLink to="/tuition-purchase" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'graduation-cap']" />
            </span>
            <span>
              Tuition Purchase
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/webstarts">
          <NuxtLink to="/webstarts" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'keyboard']" />
            </span>
            <span>
              Webstart
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/quickuser">
          <NuxtLink to="/quickuser" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'keyboard']" />
            </span>
            <span>
              Quick User
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/renters-coverage-confirmations">
          <NuxtLink to="/renters-coverage-confirmations" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'chart-network']" />
            </span>
            <span>
              Renters Coverage Confirmations
            </span>
          </NuxtLink>
        </MenuItem>
      </SubMenu>
      <SubMenu key="Partners" title="Partners">
        <template #icon>
          <FaIcon :icon="['fas', 'handshake']" />
        </template>
        <MenuItem key="/schools">
          <NuxtLink to="/schools" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'school']" />
            </span>
            <span>
              Schools
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/properties">
          <NuxtLink to="/properties" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'building']" />
            </span>
            <span>
              Properties
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/template-set">
          <NuxtLink to="/template-sets" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'keyboard']" />
            </span>
            <span>
              Template Sets
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="isTuitionImpl || isRentersImpl" key="/client_groups">
          <NuxtLink to="/client_groups" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'chart-network']" />
            </span>
            <span>
              Client Groups
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="isTuitionImpl || isRentersImpl" key="/ref_code_client_groups">
          <NuxtLink to="/ref_code_client_groups" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'chart-network']" />
            </span>
            <span>
              RefCode Client Groups
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/contracts">
          <NuxtLink to="/contracts" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'file-contract']" />
            </span>
            <span>
              Contracts
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="!config.isProd && idImplementationRights" key="/promote-bulk">
          <NuxtLink to="/promote-bulk" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'chart-network']" />
            </span>
            <span>
              Promote Bulk
            </span>
          </NuxtLink>
        </MenuItem>
      </SubMenu>
      <SubMenu key="Partner Portal" title="Partner Portal">
        <template #icon>
          <FaIcon :icon="['far', 'handshake']" />
        </template>
        <MenuItem v-if="idPpAdminRights" key="/partner-portal">
          <NuxtLink to="/partner-portal" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'clock']" />
            </span>
            <span>
              Partners
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="idPpAdminRights" key="/partner-portal/contacts">
          <NuxtLink to="/partner-portal/contacts" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'clock']" />
            </span>
            <span>
              Contacts
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="idPpAdminRights" key="/partner-portal/roles">
          <NuxtLink to="/partner-portal/roles" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'clock']" />
            </span>
            <span>
              Roles
            </span>
          </NuxtLink>
        </MenuItem>
      </SubMenu>
      <SubMenu key="Offer Management" title="Offer Management">
        <template #icon>
          <FaIcon :icon="['fas', 'tags']" />
        </template>
        <MenuItem key="/offers-list">
          <NuxtLink to="/offers" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'typewriter']" />
            </span>
            <span>
              Offers
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/tracking-codes">
          <NuxtLink to="/tracking-codes" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'barcode']" />
            </span>
            <span>
              Tracking Codes
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="licensedRights()" key="/moratoriums">
          <NuxtLink
            to="/moratoriums"
            class="flex items-center"
            data-test-id="moratoriums"
          >
            <span class="w-7">
              <FaIcon :icon="['fas', 'clock']" />
            </span>
            <span>
              Moratoriums
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="isSuperUser" key="/template-token">
          <NuxtLink to="/template-token" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'keyboard']" />
            </span>
            <span>
              Template Tokens
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="isSuperUser" key="/abtests">
          <NuxtLink to="/abtests" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'keyboard']" />
            </span>
            <span>
              A/B Test
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="isSuperUser" key="/api-clients">
          <NuxtLink to="/api-clients" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'network-wired']" />
            </span>
            <span>
              API Clients
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="isSuperUser" key="/routes">
          <NuxtLink to="/routes" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'route']" />
            </span>
            <span>
              Routes
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/bypass">
          <NuxtLink to="/bypass" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'person-sledding']" />
            </span>
            <span>
              Eligibility Bypass
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="showImplementationRollover" key="/implementation-rollover">
          <NuxtLink to="/implementation-rollover" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'copy']" />
            </span>
            <span>
              Implementation Rollover
            </span>
          </NuxtLink>
        </MenuItem>
      </SubMenu>
      <SubMenu key="Utilities" title="Utilities">
        <template #icon>
          <FaIcon :icon="['fas', 'tools']" />
        </template>
        <MenuItem v-if="canAccessFileImports" key="/file-imports">
          <NuxtLink to="/file-imports" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'file-import']" />
            </span>
            <span>
              File Imports
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/files">
          <NuxtLink to="/files" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'file']" />
            </span>
            <span>
              Files
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/file-ai-prompts">
          <NuxtLink to="/file-ai-prompts" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'wand-magic-sparkles']" />
            </span>
            <span>
              File AI Prompts
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/tuition-quote">
          <NuxtLink to="/tuition-quote" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'dollar-sign']" />
            </span>
            <span>
              Tuition Quote
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/tuition-offer">
          <NuxtLink to="/tuition-offer" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['far', 'envelope-open-dollar']" />
            </span>
            <span>
              Tuition Offer
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/tuition-widget">
          <NuxtLink to="/tuition-widget" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'dollar-sign']" />
            </span>
            <span>
              Tuition Widget
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/auditQuery">
          <NuxtLink to="/auditQuery" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'dollar-sign']" />
            </span>
            <span>
              Audit Query (Nightly Query)
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="canAccessMissingTransactions" key="/missing-transactions">
          <NuxtLink to="/missing-transactions" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'location-question']" />
            </span>
            <span>
              Missing Transactions
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem v-if="isSuperUser" key="/cli-commands">
          <NuxtLink to="/cli-commands" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'chart-network']" />
            </span>
            <span>
              CLI Commands
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/aga-grid">
          <NuxtLink to="/aga-grid" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'earth-americas']" />
            </span>
            <span>
              AGA Rates and Products
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/renters-price-calculator">
          <NuxtLink to="/renters-price-calculator" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'dollar-sign']" />
            </span>
            <span>
              Renters Price Calculator
            </span>
          </NuxtLink>
        </MenuItem>
      </SubMenu>
      <SubMenu key="Reports" title="Reports">
        <template #icon>
          <FaIcon :icon="['far', 'file-lines']" />
        </template>
        <MenuItem key="/payment-links">
          <NuxtLink to="/payment-links" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'link']" />
            </span>
            <span>
              Payment Links
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/transactional-sms">
          <NuxtLink to="/transactional-sms" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'sms']" />
            </span>
            <span>
              Transactional SMS
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/report-commands">
          <NuxtLink to="/report-commands" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'chart-network']" />
            </span>
            <span>
              Report Commands
            </span>
          </NuxtLink>
        </MenuItem>
      </SubMenu>
      <SubMenu key="GradGuard" title="GradGuard">
        <template #icon>
          <FaIcon :icon="['fas', 'graduation-cap']" />
        </template>
        <MenuItem v-if="isSuperUser" key="/agents">
          <NuxtLink to="/agents" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'id-badge']" />
            </span>
            <span>
              Agents
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/agency-hours">
          <NuxtLink to="/agency-hours" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'clock']" />
            </span>
            <span>
              Agency Hours
            </span>
          </NuxtLink>
        </MenuItem>
        <MenuItem key="/press">
          <NuxtLink to="/press" class="flex items-center">
            <span class="w-7">
              <FaIcon :icon="['fas', 'dollar-sign']" />
            </span>
            <span>
              Press
            </span>
          </NuxtLink>
        </MenuItem>
      </SubMenu>
    </AMenu>
  </div>
</template>
